import { r as registerInstance, c as createEvent, h, H as Host, g as getElement } from './index-61528ac5.js';
import { v as version } from './package-27c9b485.js';
import { f as fadeOut, a as fadeIn } from './fade-7fd71785.js';

/**
 * Invoke the `componentOnReady` method if it is available, simulate it otherwise
 * @see https://stenciljs.com/docs/api#componentonready
 */
const componentOnReady = (el) => {
    if (typeof el.componentOnReady === 'function') {
        return el.componentOnReady();
    }
    else {
        return new Promise(resolve => customOnReady(() => {
            resolve(el);
        }));
    }
};
const customOnReady = (callback) => {
    if (typeof requestAnimationFrame === 'function') {
        return requestAnimationFrame(callback);
    }
    return setTimeout(callback);
};

const postTabsCss = ".tabs-wrapper{position:relative;padding-top:1rem;border:0;background-color:rgba(var(--post-contrast-color-rgb), 0.02)}.tabs-wrapper::after{content:\"\";position:absolute;bottom:0;width:100%;height:1px;background-color:#ccc}.tabs-wrapper .tabs{background-color:rgba(0,0,0,0) !important}@media (forced-colors: active),(-ms-high-contrast: active),(-ms-high-contrast: white-on-black){.tabs-wrapper::after{background-color:ButtonBorder}}.tabs{margin:0;padding:0;list-style:none;display:flex;flex-wrap:nowrap;overflow-x:auto;overscroll-behavior-x:contain;white-space:nowrap}.tabs::after{content:\"\";display:block;flex:1 0 auto;width:1rem}.tab-content{padding-top:1rem}.bg-yellow .tabs-wrapper{background-color:#fc0}.bg-white .tabs-wrapper{background-color:#fff}.bg-light .tabs-wrapper{background-color:#faf9f8}.bg-gray .tabs-wrapper{background-color:#f4f3f1}.bg-dark .tabs-wrapper{background-color:#333}.bg-black .tabs-wrapper{background-color:#000}.bg-primary .tabs-wrapper{background-color:#333}.bg-secondary .tabs-wrapper{background-color:#666}.bg-success .tabs-wrapper{background-color:#2c871d}.bg-warning .tabs-wrapper{background-color:#f49e00}.bg-error .tabs-wrapper{background-color:#a51728}.bg-info .tabs-wrapper{background-color:#cce4ee}.bg-success-background .tabs-wrapper{background-color:#c0debb}.bg-warning-background .tabs-wrapper{background-color:#fce2b2}.bg-error-background .tabs-wrapper{background-color:#ffdade}.bg-nightblue .tabs-wrapper{background-color:#004976}.bg-nightblue-bright .tabs-wrapper{background-color:#0076a8}.bg-petrol .tabs-wrapper{background-color:#006d68}.bg-petrol-bright .tabs-wrapper{background-color:#00968f}.bg-coral .tabs-wrapper{background-color:#9e2a2f}.bg-coral-bright .tabs-wrapper{background-color:#e03c31}.bg-olive .tabs-wrapper{background-color:#716135}.bg-olive-bright .tabs-wrapper{background-color:#aa9d2e}.bg-purple .tabs-wrapper{background-color:#80276c}.bg-purple-bright .tabs-wrapper{background-color:#c5299b}.bg-aubergine .tabs-wrapper{background-color:#523178}.bg-aubergine-bright .tabs-wrapper{background-color:#7566a0}:host{display:block}";
const PostTabsStyle0 = postTabsCss;

const PostTabs = class {
    constructor(hostRef) {
        registerInstance(this, hostRef);
        this.postChange = createEvent(this, "postChange", 7);
        this.isLoaded = false;
        this.activePanel = undefined;
    }
    get tabs() {
        return Array.from(this.host.querySelectorAll(':scope > post-tab-header'));
    }
    componentDidLoad() {
        var _a;
        this.moveMisplacedTabs();
        this.enableTabs();
        const initiallyActivePanel = this.activePanel || ((_a = this.tabs[0]) === null || _a === void 0 ? void 0 : _a.panel);
        void this.show(initiallyActivePanel);
        this.isLoaded = true;
    }
    /**
     * Shows the panel with the given name and selects its associated tab.
     * Any other panel that was previously shown becomes hidden and its associated tab is unselected.
     */
    async show(panelName) {
        var _a;
        // do nothing if the tab is already active
        if (panelName === ((_a = this.activeTab) === null || _a === void 0 ? void 0 : _a.panel)) {
            return;
        }
        const previousTab = this.activeTab;
        const newTab = this.host.querySelector(`post-tab-header[panel=${panelName}]`);
        this.activateTab(newTab);
        // if a panel is currently being displayed, remove it from the view and complete the associated animation
        if (this.showing) {
            this.showing.effect['target'].style.display = 'none';
            this.showing.finish();
        }
        // hide the currently visible panel only if no other animation is running
        if (previousTab && !this.showing && !this.hiding)
            this.hidePanel(previousTab.panel);
        // wait for any hiding animation to complete before showing the selected tab
        if (this.hiding)
            await this.hiding.finished;
        this.showSelectedPanel();
        // wait for any display animation to complete for the returned promise to fully resolve
        if (this.showing)
            await this.showing.finished;
        if (this.isLoaded)
            this.postChange.emit(this.activeTab.panel);
    }
    moveMisplacedTabs() {
        if (!this.tabs)
            return;
        this.tabs.forEach(tab => {
            if (tab.getAttribute('slot') === 'tabs')
                return;
            tab.setAttribute('slot', 'tabs');
        });
    }
    enableTabs() {
        var _a;
        if (!this.tabs)
            return;
        this.tabs.forEach(async (tab) => {
            await componentOnReady(tab);
            // if the tab has an "aria-controls" attribute it was already linked to its panel: do nothing
            if (tab.getAttribute('aria-controls'))
                return;
            const tabPanel = this.getPanel(tab.panel);
            tab.setAttribute('aria-controls', tabPanel.id);
            tabPanel.setAttribute('aria-labelledby', tab.id);
            tab.addEventListener('click', () => {
                void this.show(tab.panel);
            });
            tab.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    void this.show(tab.panel);
                }
            });
            tab.addEventListener('keydown', (e) => {
                if (e.key === 'ArrowRight' || e.key === 'ArrowLeft')
                    this.navigateTabs(tab, e.key);
            });
        });
        // if the currently active tab was removed from the DOM then select the first one
        if (this.activeTab && !this.activeTab.isConnected) {
            void this.show((_a = this.tabs[0]) === null || _a === void 0 ? void 0 : _a.panel);
        }
    }
    activateTab(tab) {
        if (this.activeTab) {
            this.activeTab.setAttribute('aria-selected', 'false');
            this.activeTab.setAttribute('tabindex', '-1');
            this.activeTab.classList.remove('active');
        }
        tab.setAttribute('aria-selected', 'true');
        tab.setAttribute('tabindex', '0');
        tab.classList.add('active');
        this.activeTab = tab;
    }
    hidePanel(panelName) {
        const previousPanel = this.getPanel(panelName);
        if (!previousPanel)
            return;
        this.hiding = fadeOut(previousPanel);
        this.hiding.onfinish = () => {
            previousPanel.style.display = 'none';
            this.hiding = null;
        };
    }
    showSelectedPanel() {
        const panel = this.getPanel(this.activeTab.panel);
        panel.style.display = 'block';
        // prevent the initially selected panel from fading in
        if (!this.isLoaded)
            return;
        this.showing = fadeIn(panel);
        this.showing.onfinish = () => {
            this.showing = null;
        };
    }
    getPanel(name) {
        return this.host.querySelector(`post-tab-panel[name=${name}]`);
    }
    navigateTabs(tab, key) {
        const activeTabIndex = Array.from(this.tabs).indexOf(tab);
        let nextTab;
        if (key === 'ArrowRight') {
            nextTab = this.tabs[activeTabIndex + 1] || this.tabs[0];
        }
        else {
            nextTab = this.tabs[activeTabIndex - 1] || this.tabs[this.tabs.length - 1];
        }
        if (!nextTab)
            return;
        nextTab.focus();
    }
    render() {
        return (h(Host, { key: '8a75146842b2d4d1418f886560f689515aa70f55', "data-version": version }, h("div", { key: '104511ac9629220381fb4f51765160c722b7ad73', class: "tabs-wrapper", part: "tabs" }, h("div", { key: '396918ce1bbdcd181241d6b78f4d2cadc81b18ad', class: "tabs", role: "tablist" }, h("slot", { key: 'd5098b3cbbb1fa11369c010d2fdc17ec9af30ea3', name: "tabs", onSlotchange: () => this.enableTabs() }))), h("div", { key: 'b021fe6f55b5f70601a2cf7800e90554926704b9', class: "tab-content", part: "content" }, h("slot", { key: '4c8df45fc295211e64bf900452df9e0f1b879bdf', onSlotchange: () => this.moveMisplacedTabs() }))));
    }
    get host() { return getElement(this); }
};
PostTabs.style = PostTabsStyle0;

export { PostTabs as P };
